import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';
import { IssuesActions } from '@states/issues/issues.action-types';
import { IssuesService } from '../../services/issues.service';
import { DeviceStabilityService } from '../../services/device-stability.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from '../../edge/utils.service';

@Injectable()
export class IssuesEffects {
  public getIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.getIssues),
      switchMap(() => {
        return this.issuesService.getIssues().pipe(
          map((issues) => {
            return IssuesActions.getIssuesSuccess({ issues });
          }),
          catchError((err) => {
            const msg = this.utilsService.errMessage(err);
            return of(
              IssuesActions.getIssuesFail({ error: err }),
              SharedActions.showMessage({
                error: this.translateService.instant('TOASTS.FAILED_TO_GET_ISSUES', { err: msg }),
              })
            );
          })
        );
      })
    )
  );

  public getCameraIssuesCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.getCameraIssuesCount),
      switchMap(() =>
        this.issuesService.getCameraIssuesCount().pipe(
          map((response) => IssuesActions.getCameraIssuesCountSuccess({ count: response.count })),
          catchError((error) => of(IssuesActions.getCameraIssuesCountFail({ error })))
        )
      )
    )
  );

  public getCoreIssuesCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.getCoreIssuesCount),
      switchMap(() =>
        this.issuesService.getCoreIssuesCount().pipe(
          map((response) => IssuesActions.getCoreIssuesCountSuccess({ count: response.count })),
          catchError((error) => of(IssuesActions.getCoreIssuesCountFail({ error })))
        )
      )
    )
  );

  public refreshIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.refreshIssues),
      mergeMap(() => [
        IssuesActions.getIssues(),
        IssuesActions.getCameraIssuesCount(),
        IssuesActions.getCoreIssuesCount(),
      ])
    )
  );

  public getCurrentIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.getCurrentIssues),
      exhaustMap(() =>
        this.issuesService.getCurrentIssues().pipe(
          map((currentIssues) => IssuesActions.getCurrentIssuesSuccess({ currentIssues })),
          catchError((error) => of(IssuesActions.getCurrentIssuesFail({ error }))),
        ),
      ),
    ),
  );

  public getIssueTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.getIssueTypes),
      exhaustMap(() =>
        this.issuesService.getIssueTypes().pipe(
          map((issueTypes) => IssuesActions.getIssueTypesSuccess({ issueTypes })),
          catchError((error) => of(IssuesActions.getIssueTypesFail({ error }))),
        ),
      ),
    ),
  );

  public setDeviceIgnore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setDeviceIgnore),
      switchMap(({ request }) =>
        this.issuesService.setDeviceIgnore(request).pipe(
          map(() => IssuesActions.setDeviceIgnoreSuccess()),
          catchError((error) =>
            of(IssuesActions.setDeviceIgnoreFail({ error, request })),
          ),
        ),
      ),
    ),
  );

  public setTypeIgnore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setTypeIgnore),
      switchMap(({ request }) =>
        this.issuesService.setTypeIgnore(request).pipe(
          map(() => IssuesActions.setTypeIgnoreSuccess()),
          catchError((error) =>
            of(IssuesActions.setTypeIgnoreFail({ error, request })),
          ),
        ),
      ),
    ),
  );

  public refreshOrganizationIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.refreshOrganizationIssues),
      mergeMap(() => [IssuesActions.getCurrentIssues(), IssuesActions.getIssueTypes()])
    )
  );

  public refreshDeviceIssuesOnIgnoreChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        IssuesActions.setDeviceIgnoreSuccess,
        IssuesActions.setTypeIgnoreSuccess,
        IssuesActions.updateDefinitionSuccess,
        IssuesActions.createDefinitionSuccess,
        IssuesActions.deleteDefinitionSuccess,
      ),
      map(() => IssuesActions.refreshIssues()),
    ),
  );

  public createDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.createDefinition),
      switchMap(({ request }) =>
        this.issuesService.createDefinition(request).pipe(
          mergeMap((definition) => [
            IssuesActions.createDefinitionSuccess({ definition }),
            IssuesActions.getIssueTypes(),
            IssuesActions.getCurrentIssues(),
          ]),
          catchError((error) => of(IssuesActions.createDefinitionFail({ error }))),
        ),
      ),
    ),
  );

  public updateDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateDefinition),
      switchMap(({ code, request }) =>
        this.issuesService.updateDefinition(code, request).pipe(
          mergeMap(() => [
            IssuesActions.updateDefinitionSuccess(),
            IssuesActions.getIssueTypes(),
            IssuesActions.getCurrentIssues(),
          ]),
          catchError((error) => of(IssuesActions.updateDefinitionFail({ error }))),
        ),
      ),
    ),
  );

  public deleteDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.deleteDefinition),
      switchMap(({ code }) =>
        this.issuesService.deleteDefinition(code).pipe(
          mergeMap(() => [
            IssuesActions.deleteDefinitionSuccess({ code }),
            IssuesActions.getCurrentIssues(),
          ]),
          catchError((error) => of(IssuesActions.deleteDefinitionFail({ error }))),
        ),
      ),
    ),
  );

  public getStabilityIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.getStabilityIssues),
      exhaustMap(() =>
        this.deviceStabilityService.getIssues().pipe(
          map((response) => IssuesActions.getStabilityIssuesSuccess({ stabilityIssues: response.issues })),
          catchError((error) => of(IssuesActions.getStabilityIssuesFail({ error }))),
        ),
      ),
    ),
  );

  public ignoreStabilityIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.ignoreStabilityIssue),
      mergeMap(({ issueId }) =>
        this.deviceStabilityService.ignoreIssue(issueId).pipe(
          map(() => IssuesActions.ignoreStabilityIssueSuccess({ issueId })),
          catchError((error) =>
            of(
              IssuesActions.ignoreStabilityIssueFail({ error, issueId }),
              SharedActions.showMessage({
                error: this.translateService.instant('ORG_ISSUES.STABILITY.IGNORE_FAILED'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public refreshCurrentIssuesOnStabilityUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.stabilityIssueUpdateFromSocket),
      map(() => IssuesActions.getCurrentIssues()),
    ),
  );

  public constructor(
    private readonly actions$: Actions,
    private readonly issuesService: IssuesService,
    private readonly deviceStabilityService: DeviceStabilityService,
    private readonly translateService: TranslateService,
    private readonly utilsService: UtilsService
  ) {}
}
